import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import type { BookingSettings } from '@/services/bookingService';

interface BookingSettingsFormProps {
  settings: BookingSettings | null;
  onChange: (settings: BookingSettings | null) => void;
  disabled?: boolean;
}

export const BookingSettingsForm: React.FC<BookingSettingsFormProps> = ({
  settings,
  onChange,
  disabled = false,
}) => {
  // Local state for input values to allow proper editing
  const [bufferTime, setBufferTime] = useState('');
  const [minHours, setMinHours] = useState('');
  const [maxDays, setMaxDays] = useState('');

  // Sync local state with settings
  useEffect(() => {
    setBufferTime(settings?.buffer_time_minutes?.toString() ?? '');
    setMinHours(settings?.advance_booking_min_hours?.toString() ?? '');
    setMaxDays(settings?.advance_booking_max_days?.toString() ?? '');
  }, [
    settings?.buffer_time_minutes,
    settings?.advance_booking_min_hours,
    settings?.advance_booking_max_days,
  ]);

  const updateSetting = <K extends keyof BookingSettings>(key: K, value: BookingSettings[K]) => {
    onChange(settings ? { ...settings, [key]: value } : null);
  };

  return (
    <div className='space-y-6'>
      {/* Booking Link Toggle */}
      <div className='flex items-center justify-between rounded-xl bg-slate-50 p-4'>
        <div>
          <Label className='font-medium'>Enable Public Booking</Label>
          <p className='text-sm text-slate-500'>Allow patients to book via your public link</p>
        </div>
        <Switch
          checked={settings?.booking_link_active ?? true}
          onCheckedChange={checked => updateSetting('booking_link_active', checked)}
          disabled={disabled}
        />
      </div>

      <Separator />

      {/* Buffer Time */}
      <div className='space-y-2'>
        <Label>Buffer Time (minutes)</Label>
        <Input
          type='number'
          min={0}
          max={60}
          value={bufferTime}
          onChange={e => setBufferTime(e.target.value)}
          onBlur={() => {
            const num = parseInt(bufferTime, 10);
            if (!isNaN(num) && num >= 0 && num <= 60) {
              updateSetting('buffer_time_minutes', num);
            } else {
              updateSetting('buffer_time_minutes', 0);
              setBufferTime('0');
            }
          }}
          disabled={disabled}
        />
        <p className='text-xs text-slate-500'>Time between appointments</p>
      </div>

      <Separator />

      {/* Advance Booking Limits */}
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        <div className='space-y-2'>
          <Label>Minimum Advance Booking (hours)</Label>
          <Input
            type='number'
            min={0}
            value={minHours}
            onChange={e => setMinHours(e.target.value)}
            onBlur={() => {
              const num = parseInt(minHours, 10);
              if (!isNaN(num) && num >= 0) {
                updateSetting('advance_booking_min_hours', num);
              } else {
                updateSetting('advance_booking_min_hours', 0);
                setMinHours('0');
              }
            }}
            disabled={disabled}
          />
          <p className='text-xs text-slate-500'>How far in advance patients must book</p>
        </div>
        <div className='space-y-2'>
          <Label>Maximum Advance Booking (days)</Label>
          <Input
            type='number'
            min={1}
            max={365}
            value={maxDays}
            onChange={e => setMaxDays(e.target.value)}
            onBlur={() => {
              const num = parseInt(maxDays, 10);
              if (!isNaN(num) && num >= 1 && num <= 365) {
                updateSetting('advance_booking_max_days', num);
              } else {
                updateSetting('advance_booking_max_days', 1);
                setMaxDays('1');
              }
            }}
            disabled={disabled}
          />
          <p className='text-xs text-slate-500'>How far into the future patients can book</p>
        </div>
      </div>

      <Separator />

      {/* Auto-accept and Cancellation */}
      {/* <div className='space-y-4'> */}
      {/* <div className='flex items-center justify-between'>
          <div>
            <Label className='font-medium'>Auto-Accept Appointments</Label>
            <p className='text-sm text-slate-500'>Automatically confirm bookings</p>
          </div>
          <Switch
            checked={settings?.auto_accept_appointments ?? true}
            onCheckedChange={checked => updateSetting('auto_accept_appointments', checked)}
            disabled={disabled}
          />
        </div> */}

      {/* <div className="flex items-center justify-between">
          <div>
            <Label className="font-medium">Allow Patient Cancellation</Label>
            <p className="text-sm text-slate-500">Let patients cancel their own appointments</p>
          </div>
          <Switch
            checked={settings?.allow_patient_cancellation ?? true}
            onCheckedChange={(checked) => updateSetting('allow_patient_cancellation', checked)}
            disabled={disabled}
          />
        </div> */}
      {/* </div> */}

      {/* Booking Page Message */}
      <div className='space-y-2'>
        <Label>Booking Page Message</Label>
        <Textarea
          value={settings?.booking_page_message ?? ''}
          onChange={e => updateSetting('booking_page_message', e.target.value)}
          placeholder='Custom message to display on your booking page...'
          rows={3}
          disabled={disabled}
        />
      </div>
    </div>
  );
};
